import React from "react";
import { motion } from "framer-motion";
import "./Pricing.css";
import { Headings } from "../../components";
import { usePopup } from "../../context/PopupContext";

const plans = [
  {
    name: "Starter",
    price: "499",
    increment: 100,
    highlight: false,
    description: "Best for small businesses just getting online.",
    features: [
      "Up to 5 pages",
      "Mobile responsive design",
      "Basic on-page SEO",
      "Contact form setup",
      "7–10 day delivery",
      "1 revision",
    ],
    cta: "Book Free Consultation",
  },
  {
    name: "Growth",
    price: "999",
    increment: 199,
    highlight: true,
    description:
      "Perfect for growing businesses that want a professional edge.",
    features: [
      "Up to 10 pages",
      "Premium design layout",
      "Speed optimization",
      "Advanced on-page SEO",
      "Analytics setup",
      "Priority delivery",
      "2 revisions",
    ],
    cta: "Book Free Consultation",
    badge: "Most Popular",
  },
  {
    name: "Pro",
    price: "1799",
    increment: 399,
    highlight: false,
    description:
      "For businesses that want maximum performance and polish.",
    features: [
      "Up to 15 pages",
      "Custom UI enhancements",
      "Technical SEO setup",
      "Performance optimization",
      "Conversion-focused layout",
      "Priority support",
      "Unlimited minor revisions",
    ],
    cta: "Book Free Consultation",
  },
];

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const formatPrice = (num) => Number(num).toLocaleString();

const PricingTable = () => {
  const { openPopup } = usePopup();
  return (
          <motion.section
      initial={{
        x: "0%",
        y: -100,
        opacity: 0,
      }}
      whileInView={{
        x: "0%",
        y: 0,
        opacity: 1,
      }}
      transition={{
        duration: 1,
        delay: 0.7,
      }}
      viewport={{ once: true }}
      className="pricing-section container py-5"
      id="pricing"
    >
      <motion.div
        initial={{ opacity: 0, y: -60 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        viewport={{ once: true }}
        className="text-center mb-5"
      >
        <Headings
          title="Simple Pricing"
          text="Pick a plan that fits your business."
        />
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="row g-4 justify-content-center"
      >
        {plans.map((plan) => {
          const current = Number(plan.price);
          const original = current + plan.increment;
          const savedPercent = Math.round(
            ((original - current) / original) * 100
          );

          return (
            <div key={plan.name} className="col-lg-4 col-md-6 col-12">
              <motion.div
                variants={cardVariants}
                className={`pricing-card ${
                  plan.highlight ? "pricing-card--highlight" : ""
                }`}
              >
                {plan.badge && (
                  <div className="pricing-badge">{plan.badge}</div>
                )}

                {/* 💎 Save badge */}
                <div className="pricing-save-badge">
                  Save {savedPercent}%
                </div>

                <h3 className="pricing-plan">{plan.name}</h3>

                {/* ✅ INLINE PRICE */}
                <div className="pricing-price-inline">
                  <span className="pricing-old-price">
                    <span className="pricing-currency">$</span>
                    <del>{formatPrice(original)}</del>
                  </span>

                  <span className="pricing-current-price-inline price-glow">
                    <span className="pricing-currency">$</span>
                    <strong>{formatPrice(current)}</strong>
                    <span className="pricing-period"> CAD</span>
                  </span>
                </div>

                <p className="pricing-description">{plan.description}</p>

                <ul className="pricing-features">
                  {plan.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      viewport={{ once: true }}
                    >
                      ✓ {feature}
                    </motion.li>
                  ))}
                </ul>

                <a
                  href="https://calendly.com/quantech/website-consultation"
                  target="_blank"
                  rel="noreferrer"
                  className={`pricing-btn ${
                    plan.highlight
                      ? "pricing-btn--primary pricing-btn--shine"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    openPopup({
                      source: `Pricing — ${plan.name} Plan`,
                      plan: plan.name,
                    });
                  }}
                >
                  {plan.cta}
                </a>
              </motion.div>
            </div>
          );
        })}
      </motion.div>

      <div className="text-center mt-4 pricing-footnote">
        No hidden fees • Secure process • Limited onboarding slots each month
      </div>
    </motion.section>
  );
};

export default PricingTable;